/**
 * The locale is pinned rather than left to the runtime, because the same list
 * is formatted on the server and again in the browser, and a server whose
 * locale differed would hand React markup it then had to throw away.
 */
const studioCapacityFormatter = new Intl.ListFormat('en-GB', {
    style: 'short',
    type: 'unit'
});

/**
 * @type {import('$types/components/utilities/format.d.ts').FormatStudioCapacity}
 *
 * The figures a producer plans a shoot by: the floor area, the seated capacity
 * and the standing capacity. Each is a field of its own, so a studio whose
 * editor recorded only some of them reads without the gaps.
 */
export const formatStudioCapacity = (studio) => studioCapacityFormatter.format(
    [
        studio?.area,
        studio?.seatedCapacity,
        studio?.standingCapacity
    ].filter(Boolean)
);

// The timezone is passed in rather than left to the runtime, for the reason the
// locale is pinned above.
const eventFormatters = new Map();

/** @type {(timezone: string, options: Intl.DateTimeFormatOptions) => Intl.DateTimeFormat} */
const eventFormatter = (timezone, options) => {
    const key = `${timezone}:${JSON.stringify(options)}`;

    if (!eventFormatters.has(key)) {
        eventFormatters.set(key, new Intl.DateTimeFormat('en-GB', {
            ...options,
            timeZone: timezone
        }));
    }

    return eventFormatters.get(key);
};

/** @type {(value: string | null | undefined, timezone: string) => string} */
const eventDay = (value, timezone) => {
    if (!value) {
        return '';
    }

    return eventFormatter(timezone, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    }).format(new Date(value)).replace(/\//g, '.');
};

/** @type {(value: string | null | undefined, timezone: string) => string} */
const eventClock = (value, timezone) => {
    if (!value) {
        return '';
    }

    return eventFormatter(timezone, {
        hour: '2-digit',
        hour12: false,
        minute: '2-digit'
    }).format(new Date(value)).replace(':', '.');
};

/**
 * @type {import('$types/components/utilities/format.d.ts').FormatEventDate}
 */
export const formatEventDate = (event, timezone) => {
    const start = eventDay(event?.startDate, timezone);
    const end = eventDay(event?.endDate, timezone);

    if (!end || end === start) {
        return start;
    }

    return `${start} — ${end}`;
};

/**
 * @type {import('$types/components/utilities/format.d.ts').FormatEventTime}
 */
export const formatEventTime = (event, timezone) => {
    const start = eventClock(event?.startDate, timezone);
    const end = eventClock(event?.endDate, timezone);

    if (!start || !end) {
        return start || end;
    }

    return `${start} — ${end}`;
};

// The box the design draws the map in at desktop, which is what the server has
// to reckon a bounding box against before anything has been measured. An embed
// is given the ground it covers rather than a centre and a zoom, so the zoom an
// editor set only means something alongside a size to spend it on.
const MAP_HEIGHT = 615;
const MAP_WIDTH = 923;

// The zoom of a map whose editor has not said how close to sit.
const MAP_ZOOM = 15;

/** @type {(position: any) => { latitude: number, longitude: number, zoom: number } | null} */
const mapPosition = (position) => {
    const latitude = position?.latitude;
    const longitude = position?.longitude;

    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
        return null;
    }

    return {
        latitude,
        longitude,
        zoom: typeof position?.zoom === 'number' ? position.zoom : MAP_ZOOM
    };
};

/**
 * @type {import('$types/components/utilities/format.d.ts').FormatMapEmbedUrl}
 */
export const formatMapEmbedUrl = (position, box) => {
    const centre = mapPosition(position);

    if (!centre) {
        return '';
    }

    const width = box?.width > 0 ? box.width : MAP_WIDTH;
    const height = box?.height > 0 ? box.height : MAP_HEIGHT;

    // Web Mercator: the world is 256 pixels wide at zoom 0 and doubles from
    // there, and a degree of latitude covers less ground the further from the
    // equator it is read.
    const lonSpan = 360 * width / (256 * Math.pow(2, centre.zoom));
    const latSpan = lonSpan * (height / width) * Math.cos(centre.latitude * Math.PI / 180);

    const bbox = [
        centre.longitude - lonSpan / 2,
        centre.latitude - latSpan / 2,
        centre.longitude + lonSpan / 2,
        centre.latitude + latSpan / 2
    ].map((degrees) => degrees.toFixed(6)).join(',');

    return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${centre.latitude},${centre.longitude}`;
};

/**
 * @type {import('$types/components/utilities/format.d.ts').FormatMapUrl}
 */
export const formatMapUrl = (position) => {
    const centre = mapPosition(position);

    if (!centre) {
        return '';
    }

    return `https://www.openstreetmap.org/?mlat=${centre.latitude}&mlon=${centre.longitude}#map=${centre.zoom}/${centre.latitude}/${centre.longitude}`;
};

export const formatLinkBlockToUrl = (data, subject) => {
    const linkTo = data?.linkTo;
    let url = '';
    switch (linkTo) {
        case 'email':
            url = 'mailto:' + data[linkTo] + `${subject ? `?subject=${subject}` : ''}`;
            break;
        case 'anchor':
            url = '#' + data[linkTo];
            break;
        case 'custom_url':
            url = data?.customUrl;
            break;
        case 'file':
            url = data[linkTo]?.url;
            break;
        case 'page':
            url = data[linkTo]?.url;
            break;
        case 'phone':
            url = 'tel:' + data[linkTo];
            break;
    }

    return url;
};
